<template>
  <div class='automated-exploitation-issue-body'>
    <span class="description-block" v-if='issue.description'>
      <div class="data-title">{{$t('detailed.issue.description.title')}}</div>
      <div class="text-container">
        {{description}}
      </div>
    </span>
    <div class="asset-block">
      <div class="data-title">{{$t('detailed.issue.asset.title')}}</div>
      <div class="text-container">
        {{asset}}
      </div>
    </div>
    <div class='tld-block'>
      <div class='details-column'>
        <div class='data-title'>{{ $t('detailed.issue.tld.title') }}</div>
        <div class='text-container'>
          {{ tld }}
        </div>
      </div>
      <div class='details-column' v-if='isDeepScan'>
        <div class='data-title'>{{ $t('detailed.issue.subdomain.title') }}</div>
        <div class='text-container'>
          {{ subdomain }}
        </div>
      </div>
    </div>
    <div class="data-row">
      <div class="details-column">
        <div class="data-title">{{$t('issueDataPopup.port')}}</div>
        <div> {{port}} </div>
      </div>
      <div class="details-column">
        <div class="data-title">{{$t('issueDataPopup.protocol')}}</div>
        <div> {{protocol}} </div>
      </div>
      <div class="details-column" v-if='service'>
        <div class="data-title">{{$t('issueDataPopup.service')}}</div>
        <div> {{service}} </div>
      </div>
      <div class="details-column" v-if='version'>
        <div class="data-title">{{$t('issueDataPopup.version')}}</div>
        <div> {{version}} </div>
      </div>
    </div>
    <div class="technical-block">
      <div class="data-title">{{$t('issueDataPopup.technical.data')}}</div>
      <div class="technical-data">
        <div class='text-container'>
          <div class='details-column technical-data' v-if='cveName'>
            <div class="data-sub-title">{{$t('issueDataPopup.technologies.cve.name')}}</div>
            <div class='text-container cve-name'>{{ cveName }}</div>
          </div>
          <div class='details-column technical-data' v-if='cvssScore'>
            <div class="data-sub-title">{{$t('issueDataPopup.technologies.cve.cvss')}}</div>
            <div class='text-container cvss-score' :style="cvssStyle">{{ cvssScore }}</div>
          </div>
          <div class='details-column technical-data' v-if='epssScore'>
            <div class="data-sub-title">{{$t('issueDataPopup.technologies.cve.epss')}}</div>
            <div class='text-container cve-epss'>
              <epss-score :value='epssFormattedScore'/>
            </div>
          </div>
          <span class="url-block" v-if='urls.length'>
            <div class="data-sub-title">{{$t('issueDataPopup.technologies.cve.url')}}</div>
              <div v-for='(url, index) in urls' :key='index'>
               <div class='url-option'>
                <div class='data-title option'>
                  <span class="material-icons bullet">
                    lens_blur
                  </span>
                </div>
               <div class='text-container'>
                 <a :href='url' target="_blank"> {{url}} </a>
               </div>
              </div>
            </div>
          </span>
        </div>
      </div>
    </div>
    <div class="cve-vector-block" v-if='vector && vectorString'>
      <div class="data-title">{{$t('issueDataPopup.cve.vector.data')}}</div>
      <div class="cve-vector-data">
        <div class='text-container'>
          <div class='details-column cve-vector-details'>
            <div class="data-sub-title">{{ $t('cve.vector.details.vectorString') }}</div>
            <div class='text-container'>{{ vectorString }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import EpssScore from '@/components/common/EpssScore.vue';
import { TEST_TYPES_HOSTS } from '@/constants/projects';

export default {
  name: 'AutomatedExploitationIssueBody',
  props: {
    issue: {
      type: Object,
      required: true,
    },
  },
  components: {
    EpssScore,
  },
  computed: {
    cvssStyle() {
      return {
        ...this.color,
        backgroundColor: this.color['background-color'],
        fontSize: '14px',
        width: '37px',
        height: '25px',
        borderRadius: '3px',
      };
    },
    color() {
      const severity = this.cvssScore;
      if (severity >= 9 && severity <= 10) {
        return {color: '#E0255E', 'background-color': '#E0255030'};
      }
      if (severity >= 7 && severity <= 8.9) {
        return {color: '#FF7F00', 'background-color': '#FF7F0030'};
      }
      if (severity >= 4 && severity <= 6.9) {
        return {color: '#2459d5', 'background-color': '#2459d530'};
      }
      return {color: '#2BAF69', 'background-color': '#2BAF6030'};
    },
    description() {
      return this.issue.metaData?.cveData?.description || this.issue.description;
    },
    asset() {
      return this.issue.asset;
    },
    tld() {
      return this.issue.tld;
    },
    subdomain() {
      return this.issue.metaData?.subdomain || '';
    },
    port() {
      return this.issue.metaData.port;
    },
    protocol() {
      return this.issue.metaData.service.toUpperCase();
    },
    version() {
      return this.issue.metaData.version;
    },
    service() {
      return this.issue.metaData?.techVersion || null;
    },
    cvssScore() {
      return this.issue.metaData.cvss;
    },
    epssScore() {
      return this.issue.metaData.epss || 0;
    },
    epssFormattedScore() {
      let epss = this.epssScore || 0;
      epss = Math.round(((epss * 100) + Number.EPSILON) * 100) / 100;
      return epss === 0 ? '' : epss;
    },
    vector() {
      return this.issue.metaData.vector;
    },
    trend() {
      return this.issue.metaData.trend;
    },
    vectorString() {
      return this.vector?.vector?.vectorString;
    },
    cveName() {
      return this.issue.metaData.cveName;
    },
    cveUrl() {
      return this.issue.metaData.cveUrl;
    },
    kbUrl() {
      return this.metaData?.kbUrl || '';
    },
    urls() {
      const urlList = [];
      urlList.push(this.cveUrl);
      if (this.kbUrl !== '') {
        urlList.push(this.kbUrl);
      }
      return urlList;
    },
    isDeepScan() {
      return this.issue.metaData.testSource === TEST_TYPES_HOSTS.deepscan && this.subdomain !== '';
    },
  },
};
</script>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';

.automated-exploitation-issue-body {
  .rest-part {
    max-height: rem(610);
    overflow-y: auto;
    scrollbar-width: thin;
    padding-left: rem(60);
    padding-top: $space-8;
    padding-bottom: rem(60);
    background-image: url("../../assets/half-cynergy-logo.svg");
    background-size: contain;
    background-position: right;
  }
  .cve-vector-block {
    padding-bottom: $space-5;
    display: block;
    max-width: rem(350);
    .cve-vector-data {
      padding-top: $space-4;
    }
  }
  .description-block {
    display: block;
    .text-container::first-letter {
      text-transform: capitalize;
    }
    .text-container {
      padding-right: rem(60);
      font-size: $font-size-base;
      letter-spacing: 0;
      line-height: rem(18);
      padding-bottom: $space-9;
    }
  }
  .url-block {
    display: block;
    padding-top: $space-2;
    .bullet {
      flex: 1;
    }
    .text-container {
      padding-left: rem(5);
      padding-right: rem(60);
      color: var(--text-primary);
      font-size: $font-size-base;
      letter-spacing: 0;
      line-height: rem(18);
      padding-bottom: $space-4;
    }
    .url-option {
      display: flex;
    }
    .material-icons {
      font-size: rem(15);
      opacity: 0.5;
      margin-top: rem(3);
    }
  }
  .epss-score {
    align-items: center;
    display: flex;
    max-width: rem(70);
    margin-left: rem(1);
  }
  .solution-block {
    display: block;
    padding-bottom: $space-5;
    .bullet {
      flex: 1;
    }
    .text-container {
      padding-left: rem(5);
      padding-right: rem(60);
      color: var(--text-primary);
      font-size: $font-size-base;
      letter-spacing: 0;
      line-height: rem(18);
      padding-bottom: $space-4;
    }
    .mitigation-option {
      display: flex;
    }
    .material-icons {
      font-size: rem(15);
      opacity: 0.5;
      margin-top: rem(3);
    }
  }
  .reference-block {
    padding-bottom: $space-5;
    display: block;
    .text-container {
      padding-right: rem(60);
      color: var(--text-primary);
      font-size: $font-size-base;
      letter-spacing: 0;
      line-height: rem(18);
      padding-bottom: $space-4;
    }
    .url-section {
      display: flex;
      .bullet {
        padding-right: rem(5);
      }
      .material-icons {
        font-size: rem(15);
        opacity: 0.5;
        margin-top: rem(1);
      }
    }
  }
  .technical-block {
    padding-bottom: $space-5;
    display: block;
    .url-section {
      .bullet {
        padding-right: rem(5);
      }
      .text-container {
        padding-left: rem(5);
        padding-right: rem(60);
        color: var(--text-primary);
        font-size: $font-size-base;
        letter-spacing: 0;
        line-height: rem(18);
        padding-bottom: $space-4;
        max-width: rem(700);
      }
      display: flex;
      .material-icons {
        font-size: rem(15);
        opacity: 0.5;
        margin-top: rem(3);
      }
    }
    .technical-data {
      padding-top: rem(4);
      .text-container {
        .details-column {
          margin-right: $space-5;
        }
        max-width: rem(400);
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
      }
    }
  }
  .data-sub-title {
    font-size: $font-size-xs;
    color: var(--text-secondary);
    letter-spacing: rem(0.8);
    line-height: $font-size-xs;
    text-transform: uppercase;
    margin-bottom: $space-1;
  }
  .data-title {
    color: var(--text-secondary);
    font-size: $font-size-sm;
    letter-spacing: rem(0.8);
    line-height: $font-size-xs;
    text-transform: uppercase;
    margin-bottom: $space-1;
  }
  .data-row {
    margin-top: $space-6;
    padding-bottom: $space-9;
    .data-column {
      display: inline-block;
      min-width: rem(90);
      max-width: rem(120);
      margin-right: $space-4;

      .probability {
        line-height: unset;
      }
    }

    .data-column-date {
      display: inline-block;
      width: rem(135);
      margin-right: $space-4;
    }
  }
  .asset-block {
    margin-bottom: $space-5;
  }
  .tld-block {
    margin-bottom: $space-5;
  }
  .subdomain-block {
    margin-bottom: $space-5;
  }
  .cvss-score {
    margin-left: rem(17);
    text-align: center;
    font-family: $font-family-semibold;
    font-size: $font-size-base;
    justify-content: center;
    letter-spacing: 0;
    line-height: $space-4;
    padding: rem(5) rem(5) rem(5) rem(6);
  }
  .details-column {
    display: inline-block;
    width: fit-content;
    max-width: rem(420);
    margin-right: $space-4;
  }
}
</style>
